<script lang="ts">
/**
 * Study stat tile (`.sb`): large `.n` value + `.l` label.
 * Global `.sb .n` uses `color: var(--g)` — pass `valueColor` to override (inline wins).
 */
export type StudySlideStatTileCardVariant =
  | 'market'
  | 'stat-inline'
  | 'imx-grid'
  | 'geo'
  | 'choro'
  | 'stripe'
  | 'payer'
  | 'pipeline'
  | 'compact'

interface Props {
  value: string | number
  /**
   * Line breaks: use a template expression with `\n` (real newline), e.g.
   * `label={\`Line one\nLine two\`}`. Plain `label="a\nb"` in markup is a literal backslash + `n`;
   * we normalize that to a newline before rendering.
   */
  label: string
  variant?: StudySlideStatTileCardVariant
  /** Main stat color (e.g. `var(--pur)`, `var(--amb)`, `var(--g)`, or a hex) */
  valueColor?: string
  /** Label lines (defaults to global muted `--mu` if omitted) */
  labelColor?: string
  /** Border + value color for `stripe` / `payer` variants */
  stripeColor?: string
  /** Extra line under label — only with `variant="payer"` */
  footer?: string
}

let {
  value,
  label,
  variant = 'market',
  valueColor,
  labelColor,
  stripeColor,
  footer,
}: Props = $props()

const stripeShell = $derived(
  stripeColor && (variant === 'stripe' || variant === 'payer')
    ? `padding:0.8rem;border-left:3px solid ${stripeColor};`
    : undefined,
)

/** Stripe/payer: colored value; else optional `valueColor` to beat global `.sb .n { color: var(--g) }` */
const valueStyle = $derived(
  stripeColor && (variant === 'stripe' || variant === 'payer')
    ? `font-size:1.45rem;color:${stripeColor};`
    : valueColor
      ? `color:${valueColor};`
      : undefined,
)

const labelStyle = $derived(labelColor ? `color:${labelColor};` : undefined)

/** HTML attributes like `label="a\nb"` pass two chars (\\ + n); JS templates pass real newlines */
const labelLines = $derived(label.replace(/\\n/g, '\n').split('\n'))
</script>

<div class="sb stat-tile stat-tile--{variant}" style={stripeShell}>
  <div class="n" style={valueStyle}>
    {value}
  </div>
  <div class="l" style={labelStyle}>
    {#each labelLines as line, i}
      {#if i > 0}<br />{/if}{line}
    {/each}
  </div>
  {#if footer && variant === 'payer'}
    <div class="stat-tile__footer">
      {footer}
    </div>
  {/if}
</div>

<style>
.stat-tile--stat-inline {
  padding: 0.85rem;
}
.stat-tile--stat-inline .n {
  font-size: 1.4rem;
}

.stat-tile--imx-grid {
  padding: 0.85rem;
}
.stat-tile--imx-grid .n {
  font-size: 1.5rem;
}

.stat-tile--geo {
  padding: 0.8rem;
}
.stat-tile--geo .n {
  font-size: 1.2rem;
}

.stat-tile--choro {
  padding: 0.8rem;
}
.stat-tile--choro .n {
  font-size: 1.1rem;
}

.stat-tile--pipeline {
  padding: 0.82rem;
}
.stat-tile--pipeline .n {
  font-size: 1.28rem;
}

.stat-tile--compact {
  padding: 0.62rem 0.65rem;
}
.stat-tile--compact .n {
  font-size: 1.12rem;
}

.stat-tile__footer {
  font-size: 0.62rem;
  color: var(--mu);
  line-height: 1.35;
  margin-top: 0.38rem;
  font-family: 'DM Mono', monospace;
}
</style>
